import React, { useState } from 'react';
import '../styles/SiteList.css';

function getStatusInfo(status) {
  switch (status) {
    case 'active':
    case 'paid':
      return { color: '#10b981', label: 'Active' };
    case 'trial':
    case 'trial_warning_sent':
      return { color: '#10b981', label: 'Trial' };
    case 'warning':
      return { color: '#f59e0b', label: 'Warning' };
    case 'past_due':
    case 'expired':
    case 'archived':
      return { color: '#ef4444', label: status.charAt(0).toUpperCase() + status.slice(1) };
    default:
      return { color: '#64748b', label: status || 'Unknown' };
  }
}

function displayName(site) {
  return site.custom_domain || site.site_id;
}

function statusTag(site) {
  const parts = [];
  if ((site.hosting_status === 'trial' || site.hosting_status === 'trial_warning_sent') && site.trial_days_remaining != null) {
    parts.push(`Trial · ${site.trial_days_remaining}d`);
  } else if (site.hosting_status) {
    parts.push(site.hosting_status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
  }
  return parts.join(' ');
}

/* ── Status group helpers ─────────────────────────────────────────────────── */

const STATUS_GROUPS = [
  { key: 'active',  label: 'ACTIVE',  match: ['active', 'paid'],                        defaultOpen: true },
  { key: 'trial',   label: 'TRIAL',   match: ['trial', 'trial_warning_sent'],            defaultOpen: false },
  { key: 'expired', label: 'EXPIRED', match: ['past_due', 'expired', 'archived'],        defaultOpen: false },
];

function groupSitesByStatus(sites) {
  const groups = {};
  STATUS_GROUPS.forEach(g => { groups[g.key] = []; });
  (sites || []).forEach(site => {
    const s = site.hosting_status || '';
    const group = STATUS_GROUPS.find(g => g.match.includes(s));
    if (group) {
      groups[group.key].push(site);
    } else {
      // Unknown status → show in active group so nothing disappears
      groups['active'].push(site);
    }
  });
  return groups;
}

/* ── Collapsible section ──────────────────────────────────────────────────── */

function StatusSection({ label, defaultOpen, sites, selectedId, onSelect }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!sites || sites.length === 0) return null;

  return (
    <div className="site-status-group">
      <button className="site-section-header" onClick={() => setOpen(!open)}>
        <span className="site-section-chevron">{open ? '▼' : '▶'}</span>
        <span className="site-section-label">{label}</span>
        <span className="site-section-count">{sites.length}</span>
      </button>
      {open && sites.map(site => {
        const status = getStatusInfo(site.hosting_status);
        return (
          <button
            key={site.id}
            className={`site-list-item ${site.id === selectedId ? 'selected' : ''}`}
            onClick={() => onSelect(site.id)}
          >
            <span
              className="site-status-dot"
              style={{ backgroundColor: status.color }}
              title={status.label}
            />
            <span className="site-list-info">
              <span className="site-list-name">{displayName(site)}</span>
              <span className="site-list-tag">{statusTag(site)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */

export default function SiteList({ sites, selectedId, onSelect, loading }) {
  if (loading) {
    return (
      <div className="site-list">
        <div className="site-list-empty">Loading sites...</div>
      </div>
    );
  }

  if (!sites || sites.length === 0) {
    return (
      <div className="site-list">
        <div className="site-list-empty">
          <p>No hosted sites yet.</p>
          <p className="site-list-hint">
            Start a conversation to build your first site.
          </p>
        </div>
      </div>
    );
  }

  const grouped = groupSitesByStatus(sites);

  return (
    <div className="site-list">
      {STATUS_GROUPS.map(g => (
        <StatusSection
          key={g.key}
          label={g.label}
          defaultOpen={g.defaultOpen}
          sites={grouped[g.key]}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
